import React, { useEffect, useMemo, useState } from 'react';
import { Customer } from '../../types';
import { fetchCustomers, createCustomer, linkProjectsToCustomer } from '../../services/crmService';
import { loadProjectsFromCloud } from '../../services/cloudService';
import {
  extractCustomerCandidates,
  clusterCandidates,
  matchAgainstExisting,
  CustomerCandidate,
} from '../../services/crmMatching';
import { findSimilarCustomers } from '../../services/customerDuplicates';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { Button } from '../Button';
import {
  X, Loader2, AlertTriangle, Building2, MapPin, Phone, Mail, User,
  Link2, GitMerge, Users, CheckCircle2, RefreshCw,
} from 'lucide-react';

interface Props {
  onClose: () => void;
  onImported: () => void;
}

// Kandidat + Verweis auf einen ggf. gefundenen bestehenden Kunden.
interface MatchedRow {
  candidate: CustomerCandidate;
  existing: { id: string; name: string; archived?: boolean };
}

interface GroupDecision {
  mode: 'merge' | 'separate' | 'skip';
  masterKey: string; // key der Variante, die bei 'merge' die Stammdaten liefert
}

// Ein Kandidat, dessen Name nur als Bauherr in den Projekten steht. Er wird
// nie vorausgewaehlt: der Bauherr ist nicht der Auftraggeber, und ein Kunde
// aus ihm anzulegen ist eine Entscheidung, die der Nutzer treffen muss.
const isClientOnly = (c: CustomerCandidate): boolean => c.role === 'bauherr';

// Hinweis auf Projekte, in denen der Name nur Bauherr ist — die werden
// bewusst NICHT verknuepft (customer_id ist die Rechnungsbeziehung).
const clientOnlyNote = (c: CustomerCandidate): string | null => {
  const n = c.clientOnlyProjectIds.length;
  if (n === 0) return null;
  return `${n} ${n === 1 ? 'Projekt' : 'Projekte'} nur als Bauherr – nicht verknüpft`;
};

const completeness = (c: CustomerCandidate): number =>
  (c.name ? 1 : 0) + (c.address ? 1 : 0) + (c.contactPerson ? 1 : 0) + (c.phone ? 1 : 0) + (c.email ? 1 : 0);

const mostComplete = (list: CustomerCandidate[]): CustomerCandidate =>
  list.reduce((best, c) => {
    const bc = completeness(best);
    const cc = completeness(c);
    if (cc > bc || (cc === bc && c.name.length > best.name.length)) return c;
    return best;
  }, list[0]);

// Der Master liefert die Stammdaten des angelegten Kunden — und damit dessen
// Rechnungsanschrift. Gewaehlt wird er deshalb NUR unter den Auftraggeber-
// Varianten: in einer gemischten Aehnlichkeitsgruppe ist der vollstaendigste
// Datensatz regelmaessig der Bauherr, und seine Anschrift landete sonst als
// Rechnungsanschrift an den Projekten des Auftraggebers. Die Rolle wiegt
// schwerer als die Vollstaendigkeit.
//
// Enthaelt die Gruppe gar keinen Auftraggeber, bleibt die ganze Gruppe die
// Auswahl — sie ist dann ohnehin mit 'skip' vorbelegt, und ohne Rueckfall
// gaebe es gar keinen Master.
const preferredMaster = (list: CustomerCandidate[]): CustomerCandidate => {
  const clients = list.filter((c) => !isClientOnly(c));
  return mostComplete(clients.length ? clients : list);
};

export const CustomerImportModal: React.FC<Props> = ({ onClose, onImported }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [candidates, setCandidates] = useState<CustomerCandidate[]>([]);
  const [existing, setExisting] = useState<Customer[]>([]);

  // Auswahl-State
  const [uniqueSelected, setUniqueSelected] = useState<Set<string>>(new Set());
  const [matchedSelected, setMatchedSelected] = useState<Set<string>>(new Set());
  const [groupDecisions, setGroupDecisions] = useState<Record<number, GroupDecision>>({});

  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Auch archivierte Kunden laden: ein Kandidat, der einem archivierten
      // Kunden entspricht, darf keine zweite Kundennummer bekommen.
      const [projects, customers] = await Promise.all([
        loadProjectsFromCloud(),
        fetchCustomers({ includeArchived: true }),
      ]);
      const cands = extractCustomerCandidates(projects);
      setExisting(customers);
      setCandidates(cands);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Projekte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Kandidaten in matched / unique / groups aufteilen.
  const { matched, unique, groups } = useMemo(() => {
    const existingLite = existing.map((c) => ({ id: c.id, name: c.name, address: c.address }));
    const byId = new Map<string, Customer>(existing.map((c) => [c.id, c]));
    const matched: MatchedRow[] = [];
    const rest: CustomerCandidate[] = [];
    for (const c of candidates) {
      // Zwei Stufen: der strikte Alt-Matcher zuerst, dann die score-basierte
      // Dublettenpruefung (Rechtsform-tolerant, prueft auch E-Mail) — beide
      // muessen dieselbe Firma nur EINMAL erkennen, sonst entsteht eine
      // zweite Kundennummer.
      const hit =
        matchAgainstExisting({ name: c.name, address: c.address }, existingLite) ??
        (() => {
          const best = findSimilarCustomers({ name: c.name, email: c.email }, existing)[0];
          return best && best.score >= 0.5
            ? { id: best.customer.id, name: best.customer.name }
            : null;
        })();
      if (hit) {
        matched.push({
          candidate: c,
          existing: { ...hit, archived: Boolean(byId.get(hit.id)?.archivedAt) },
        });
      } else rest.push(c);
    }
    const clustered = clusterCandidates(rest);
    return { matched, unique: clustered.unique, groups: clustered.groups };
  }, [candidates, existing]);

  // Default-Auswahl setzen, sobald die Aufteilung steht.
  useEffect(() => {
    // Vorausgewaehlt wird nur, was in mindestens einem Projekt wirklich
    // Auftraggeber ist. Reine Bauherren stehen zur Auswahl, aber unangehakt.
    setUniqueSelected(new Set(unique.filter((c) => !isClientOnly(c)).map((c) => c.key)));
    setMatchedSelected(
      new Set(matched.filter((m) => !isClientOnly(m.candidate)).map((m) => m.candidate.key)),
    );
    const decisions: Record<number, GroupDecision> = {};
    groups.forEach((g, i) => {
      const allClients = g.every(isClientOnly);
      decisions[i] = {
        mode: allClients ? 'skip' : 'merge',
        masterKey: preferredMaster(g).key,
      };
    });
    setGroupDecisions(decisions);
  }, [unique, matched, groups]);

  const toggle = (set: Set<string>, key: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setter(next);
  };

  // Zusammenfassung: wie viele Kunden werden angelegt / Projekte verknuepft.
  const summary = useMemo(() => {
    let create = 0;
    let link = 0;
    for (const c of unique) {
      if (uniqueSelected.has(c.key)) {
        create += 1;
        link += c.projectIds.length;
      }
    }
    groups.forEach((g, i) => {
      const d = groupDecisions[i];
      if (!d || d.mode === 'skip') return;
      if (d.mode === 'merge') {
        create += 1;
        link += g.reduce((s, c) => s + c.projectIds.length, 0);
      } else {
        create += g.length;
        link += g.reduce((s, c) => s + c.projectIds.length, 0);
      }
    });
    for (const m of matched) {
      if (matchedSelected.has(m.candidate.key)) link += m.candidate.projectIds.length;
    }
    return { create, link };
  }, [unique, uniqueSelected, groups, groupDecisions, matched, matchedSelected]);

  // Aus einem Kandidaten einen neuen Kunden anlegen und Projekte verknuepfen.
  const createFromCandidate = async (c: CustomerCandidate, projectIds: string[]) => {
    const created = await createCustomer({
      name: c.name,
      address: c.address,
      email: c.email,
      phone: c.phone,
      contacts: c.contactPerson
        ? [{ id: crypto.randomUUID(), name: c.contactPerson, phone: c.phone, email: c.email }]
        : [],
      // Kein Zahlungsziel: der Import kennt keine Vereinbarung, und eine
      // Vorbelegung wuerde die Grundeinstellung des Rechnungsmoduls
      // dauerhaft verdecken.
      defaultDiscountPercent: 0,
    });
    await linkProjectsToCustomer(created.id, projectIds);
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);

    // Arbeitsschritte einsammeln.
    const createSteps: { candidate: CustomerCandidate; projectIds: string[] }[] = [];
    for (const c of unique) {
      if (uniqueSelected.has(c.key)) createSteps.push({ candidate: c, projectIds: c.projectIds });
    }
    groups.forEach((g, i) => {
      const d = groupDecisions[i];
      if (!d || d.mode === 'skip') return;
      if (d.mode === 'merge') {
        const master = g.find((c) => c.key === d.masterKey) ?? preferredMaster(g);
        const projectIds: string[] = Array.from(new Set<string>(g.flatMap((c) => c.projectIds)));
        // Stammdaten vom Master, fehlende Felder aus den uebrigen Varianten
        // ergaenzen (erster nicht-leerer Wert gewinnt). ProjectIds sind bereits vereint.
        const fill = (field: 'address' | 'contactPerson' | 'phone' | 'email'): string | undefined =>
          master[field] || g.map((c) => c[field]).find((v) => !!v) || undefined;
        const merged: CustomerCandidate = {
          ...master,
          address: fill('address'),
          contactPerson: fill('contactPerson'),
          phone: fill('phone'),
          email: fill('email'),
          projectIds,
        };
        createSteps.push({ candidate: merged, projectIds });
      } else {
        for (const c of g) createSteps.push({ candidate: c, projectIds: c.projectIds });
      }
    });
    const linkSteps: { existingId: string; projectIds: string[] }[] = [];
    for (const m of matched) {
      if (matchedSelected.has(m.candidate.key)) {
        linkSteps.push({ existingId: m.existing.id, projectIds: m.candidate.projectIds });
      }
    }

    const total = createSteps.length + linkSteps.length;
    setProgress({ done: 0, total });

    try {
      let done = 0;
      for (const step of createSteps) {
        await createFromCandidate(step.candidate, step.projectIds);
        done += 1;
        setProgress({ done, total });
      }
      for (const step of linkSteps) {
        await linkProjectsToCustomer(step.existingId, step.projectIds);
        done += 1;
        setProgress({ done, total });
      }
      onImported();
      onClose();
    } catch (e) {
      setError('Import fehlgeschlagen: ' + rechtFehlerOder(e));
      setProgress(null);
    } finally {
      setSaving(false);
    }
  };

  const nothingToDo = summary.create === 0 && summary.link === 0;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="bg-slate-900 text-white p-6 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30"><Users className="w-6 h-6 text-blue-400" /></div>
            <div>
              <h3 className="font-bold text-lg">Kunden aus Projekten importieren</h3>
              <p className="text-xs text-slate-400">Kundendaten werden aus vorhandenen Projekten erkannt und gruppiert.</p>
            </div>
          </div>
          <button onClick={onClose} disabled={saving} className="hover:bg-white/10 p-2 rounded-full transition-all disabled:opacity-40"><X className="w-5 h-5" /></button>
        </div>

        {/* Inhalt */}
        <div className="p-6 space-y-6 overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
              <p className="text-sm font-medium">Projekte werden analysiert…</p>
            </div>
          ) : error ? (
            <div className="py-10 text-center">
              <div className="bg-red-50 p-4 rounded-full w-fit mx-auto mb-4"><AlertTriangle className="w-8 h-8 text-red-500" /></div>
              <p className="font-bold text-slate-700">Fehler</p>
              <p className="text-xs text-slate-500 mt-1 mb-4">{error}</p>
              <Button variant="secondary" onClick={load} icon={<RefreshCw className="w-4 h-4" />}>Erneut versuchen</Button>
            </div>
          ) : candidates.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <div className="bg-slate-50 p-4 rounded-full w-fit mx-auto mb-4"><Users className="w-10 h-10 opacity-20" /></div>
              <p className="font-bold text-slate-600">Keine importierbaren Kundendaten gefunden</p>
              <p className="text-xs">In den Projekten sind keine (noch nicht verknüpften) Kundennamen hinterlegt.</p>
            </div>
          ) : (
            <>
              {/* (1) Eindeutige Kunden */}
              {unique.length > 0 && (
                <section className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Eindeutige Kunden ({uniqueSelected.size}/{unique.length})
                  </h4>
                  {unique.some(isClientOnly) && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      „Nur Bauherr“ heißt: im Projekt steht kein Auftraggeber. Der Bauherr ist nicht
                      automatisch der Rechnungsempfänger — darum unangehakt, und seine Projekte werden
                      auch beim Anlegen nicht verknüpft.
                    </p>
                  )}
                  <div className="space-y-2">
                    {unique.map((c) => (
                      <label key={c.key} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={uniqueSelected.has(c.key)}
                          onChange={() => toggle(uniqueSelected, c.key, setUniqueSelected)}
                          className="mt-1 w-4 h-4 accent-blue-600"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="font-bold text-slate-800 truncate">{c.name}</span>
                            {isClientOnly(c) && (
                              <span className="text-[11px] font-bold uppercase bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 shrink-0">
                                Nur Bauherr
                              </span>
                            )}
                            <span className="text-[11px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full shrink-0">
                              {c.projectIds.length} {c.projectIds.length === 1 ? 'Projekt' : 'Projekte'}
                            </span>
                          </div>
                          {clientOnlyNote(c) && (
                            <div className="mt-1 text-[11px] text-amber-700">{clientOnlyNote(c)}</div>
                          )}
                          <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5">
                            {c.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-300" />{c.address}</span>}
                            {c.contactPerson && <span className="flex items-center gap-1"><User className="w-3 h-3 text-slate-300" />{c.contactPerson}</span>}
                            {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-300" />{c.phone}</span>}
                            {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-300" />{c.email}</span>}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </section>
              )}

              {/* (2) Aehnliche Kandidaten */}
              {groups.length > 0 && (
                <section className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                    <GitMerge className="w-4 h-4 text-amber-500" />
                    Ähnliche Kandidaten – bitte entscheiden ({groups.length})
                  </h4>
                  <div className="space-y-4">
                    {groups.map((g, i) => {
                      const d = groupDecisions[i] ?? { mode: 'merge' as const, masterKey: g[0].key };
                      return (
                        <div key={i} className="p-4 rounded-2xl border border-amber-200 bg-amber-50/40 space-y-3">
                          {/* Modus */}
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                              <input
                                type="radio"
                                name={`group-${i}-mode`}
                                checked={d.mode === 'merge'}
                                onChange={() => setGroupDecisions((prev) => ({ ...prev, [i]: { ...d, mode: 'merge' } }))}
                                className="w-4 h-4 accent-blue-600"
                              />
                              Zusammenführen
                            </label>
                            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                              <input
                                type="radio"
                                name={`group-${i}-mode`}
                                checked={d.mode === 'separate'}
                                onChange={() => setGroupDecisions((prev) => ({ ...prev, [i]: { ...d, mode: 'separate' } }))}
                                className="w-4 h-4 accent-blue-600"
                              />
                              Getrennt anlegen
                            </label>
                            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                              <input
                                type="radio"
                                name={`group-${i}-mode`}
                                checked={d.mode === 'skip'}
                                onChange={() => setGroupDecisions((prev) => ({ ...prev, [i]: { ...d, mode: 'skip' } }))}
                                className="w-4 h-4 accent-blue-600"
                              />
                              Nicht importieren
                            </label>
                          </div>
                          {g.every(isClientOnly) && (
                            <p className="text-xs text-amber-700">
                              In keinem dieser Projekte ist ein Auftraggeber hinterlegt — die Namen
                              stammen vom Bauherrn. Darum vorbelegt mit „Nicht importieren“.
                            </p>
                          )}

                          {/* Varianten nebeneinander */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {g.map((c) => (
                              <div key={c.key} className="p-3 rounded-xl bg-white border border-slate-200 space-y-1.5">
                                {d.mode === 'merge' && (
                                  <label className="flex items-center gap-2 text-[11px] font-bold text-blue-700 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`group-${i}-master`}
                                      checked={d.masterKey === c.key}
                                      onChange={() => setGroupDecisions((prev) => ({ ...prev, [i]: { ...d, masterKey: c.key } }))}
                                      className="w-3.5 h-3.5 accent-blue-600"
                                    />
                                    Stammdaten übernehmen
                                  </label>
                                )}
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                                  <span className="font-bold text-slate-800 text-sm truncate">{c.name}</span>
                                  {isClientOnly(c) && (
                                    <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 shrink-0">
                                      Bauherr
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-slate-500 space-y-0.5">
                                  {c.address && <div className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 text-slate-300 shrink-0" /><span className="truncate">{c.address}</span></div>}
                                  {c.contactPerson && <div className="flex items-center gap-1"><User className="w-3 h-3 text-slate-300" />{c.contactPerson}</div>}
                                  <div className="text-[11px] font-bold text-slate-400">{c.projectIds.length} {c.projectIds.length === 1 ? 'Projekt' : 'Projekte'}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* (3) Bereits vorhandene Kunden */}
              {matched.length > 0 && (
                <section className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-blue-500" />
                    Vorhandenem Kunden zuordnen ({matchedSelected.size}/{matched.length})
                  </h4>
                  <div className="space-y-2">
                    {matched.map((m) => (
                      <label key={m.candidate.key} className="flex items-start gap-3 p-3 rounded-xl border border-blue-100 bg-blue-50/40 hover:bg-blue-50 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={matchedSelected.has(m.candidate.key)}
                          onChange={() => toggle(matchedSelected, m.candidate.key, setMatchedSelected)}
                          className="mt-1 w-4 h-4 accent-blue-600"
                        />
                        <div className="min-w-0 flex-1 text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-800 truncate">{m.candidate.name}</span>
                            {isClientOnly(m.candidate) && (
                              <span className="text-[11px] font-bold uppercase bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 shrink-0">
                                Nur Bauherr
                              </span>
                            )}
                            <span className="text-slate-400">→</span>
                            <span className="font-bold text-blue-700 truncate">{m.existing.name}</span>
                            {m.existing.archived && (
                              <span className="text-[11px] font-bold uppercase bg-slate-100 text-slate-500 rounded px-1.5 py-0.5 shrink-0">Archiviert</span>
                            )}
                            <span className="text-[11px] font-bold text-slate-400 bg-white px-2 py-0.5 rounded-full shrink-0">
                              {m.candidate.projectIds.length} {m.candidate.projectIds.length === 1 ? 'Projekt' : 'Projekte'}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && candidates.length > 0 && (
          <div className="p-6 bg-slate-50 border-t flex flex-col sm:flex-row justify-between items-center gap-3 shrink-0">
            <div className="text-xs text-slate-500">
              {progress
                ? `Verarbeite ${progress.done}/${progress.total}…`
                : `${summary.create} ${summary.create === 1 ? 'Kunde' : 'Kunden'} anlegen · ${summary.link} ${summary.link === 1 ? 'Projekt' : 'Projekte'} verknüpfen`}
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={onClose} disabled={saving}>Abbrechen</Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={saving || nothingToDo}
                className="shadow-lg border-b-2 border-blue-700"
                icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              >
                {summary.create} anlegen / {summary.link} verknüpfen
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
